import React, { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { useSearchParams } from 'react-router-dom';
import api, { getApiErrorMessage } from '@/Services/api';
import { trackEvent } from '@/Services/analytics';
import useContactSectionData from '@/hooks/useContactSectionData';
import SeoMeta from '@/components/seo/SeoMeta';
import { contact, templateData, websiteSettings } from '@/data/content';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import { SpinnerIcon } from '@/components/ui/Icons';
import { useToast } from '@/Context/ToastContext';

const inputClass =
    'w-full px-4 py-3 rounded-xl border border-app-muted/30 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-app-text dark:text-white placeholder-app-muted focus:bg-white dark:focus:bg-gray-600 focus:ring-2 focus:ring-app-primary/20 focus:border-app-primary outline-none transition-all duration-200 text-sm';
const labelClass = 'block text-sm font-semibold text-app-text dark:text-gray-300 mb-1.5';

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
};

const Contact: React.FC = () => {
    const [searchParams] = useSearchParams();
    const contactSection = useContactSectionData();
    const [formData, setFormData] = useState({
        dzSubject: contact.default_subject,
        dzName: '',
        dzEmail: '',
        dzPhoneNumber: '',
        dzMessage: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const { showToast } = useToast();

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.dzName.trim()) {
            newErrors.dzName = contact.form_errors.name_required;
        }
        if (!formData.dzEmail.trim()) {
            newErrors.dzEmail = contact.form_errors.email_invalid;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.dzEmail.trim())) {
            newErrors.dzEmail = contact.form_errors.email_invalid;
        }
        if (!formData.dzMessage.trim()) {
            newErrors.dzMessage = contact.form_errors.message_required;
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        const slug = searchParams.get('s') || '';
        const subject = contact.slug_to_subject[slug] ?? contact.default_subject;
        setFormData((prev) => ({ ...prev, dzSubject: subject }));
    }, [searchParams]);

    const phone = websiteSettings.restaurant_phonenumber;
    const whatsapp = websiteSettings.restaurant_whatsapp;
    const email = websiteSettings.restaurant_email;
    const address = [websiteSettings.restaurant_address, websiteSettings.restaurant_city].filter(Boolean).join(', ');
    const holidays = Array.isArray(contactSection.data?.holidays) ? contactSection.data.holidays : [];
    const socialLinks = {
        instagram: websiteSettings.social_instagram,
        facebook: websiteSettings.social_facebook,
        whatsapp: websiteSettings.restaurant_whatsapp,
        whatsappChannel: websiteSettings.social_whatsapp_channel,
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);

        try {
            await api.post('/api/contact', { ...formData });
            trackEvent('contact_form_success', { subject: formData.dzSubject });
            showToast('success', contact.success_message);
            setFormData({
                dzSubject: contact.default_subject,
                dzName: '',
                dzEmail: '',
                dzPhoneNumber: '',
                dzMessage: '',
            });
        } catch (error) {
            showToast('error', getApiErrorMessage(error, contact.error_message));
        } finally {
            setSubmitting(false);
        }
    };

    const InfoRow = ({
        icon,
        label,
        value,
        href,
        delay = 0,
    }: {
        icon: React.ReactNode;
        label: string;
        value: string;
        href?: string;
        delay?: number;
    }) => {
        const content = (
            <motion.div {...fadeInUp} transition={{ delay }} className="flex items-center gap-4 group">
                <div className="w-12 h-12 flex-shrink-0 bg-app-primary/10 text-app-primary rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    {icon}
                </div>
                <div>
                    <div className="text-xs text-app-muted dark:text-gray-500 font-black uppercase tracking-widest">
                        {label}
                    </div>
                    <div className="text-base font-bold text-app-text dark:text-white group-hover:text-app-primary transition-colors">
                        {value}
                    </div>
                </div>
            </motion.div>
        );
        if (href) {
            return (
                <a href={href} className="transition-opacity">
                    {content}
                </a>
            );
        }
        return <div>{content}</div>;
    };

    return (
        <>
            <SeoMeta page="contact" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
            <div className="grid md:grid-cols-5 gap-10 lg:gap-16 items-start">
                {/* ── Left: info panel ── */}
                <div className="md:col-span-2 space-y-6">
                    <div className="flex justify-start">
                        <Button to="/" size="sm" variant="outline" className="px-6 py-2">
                            ← {templateData.back_home}
                        </Button>
                    </div>
                    <motion.div {...fadeInUp}>
                        <h1 className="text-4xl md:text-5xl font-black text-app-text dark:text-white mb-4 tracking-tight">
                            {contact.info.page_title}
                        </h1>
                        <p className="text-lg text-app-muted dark:text-gray-400 leading-relaxed">
                            {contact.info.page_subtitle}
                        </p>
                    </motion.div>

                    <div className="flex flex-col gap-6">
                        {phone && (
                            <InfoRow
                                href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                                label={contact.info.label_phone}
                                value={phone}
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.27 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.18 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                    </svg>
                                }
                                delay={0.1}
                            />
                        )}
                        {whatsapp && (
                            <InfoRow
                                href={`https://wa.me/${whatsapp.replace(/[^\d]/g, '')}`}
                                label={contact.info.label_whatsapp}
                                value={whatsapp}
                                icon={
                                    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                                        <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.46 0 .08 5.37.08 11.98c0 2.11.55 4.17 1.6 5.99L0 24l6.2-1.63a11.92 11.92 0 0 0 5.86 1.5h.01c6.6 0 11.98-5.37 11.98-11.98 0-3.2-1.25-6.21-3.53-8.4ZM12.07 21.8a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.68.97.98-3.59-.23-.37a9.78 9.78 0 0 1-1.5-5.24c0-5.42 4.41-9.82 9.84-9.82 2.62 0 5.09 1.02 6.94 2.88a9.75 9.75 0 0 1 2.88 6.94c0 5.42-4.42 9.82-9.84 9.82Zm5.39-7.36c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.28-.47-2.44-1.49-.9-.8-1.5-1.78-1.67-2.08-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.12 3.24 5.14 4.55.72.31 1.29.5 1.73.64.73.23 1.39.2 1.91.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z" />
                                    </svg>
                                }
                                delay={0.15}
                            />
                        )}
                        {email && (
                            <InfoRow
                                href={`mailto:${email}`}
                                label={contact.info.label_email}
                                value={email}
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                }
                                delay={0.2}
                            />
                        )}
                        {address && (
                            <InfoRow
                                label={contact.info.label_address}
                                value={address}
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                }
                                delay={0.25}
                            />
                        )}
                    </div>

                    {(socialLinks.instagram || socialLinks.facebook || socialLinks.whatsapp) && (
                        <motion.div
                            {...fadeInUp}
                            transition={{ delay: 0.3 }}
                            className="bg-white dark:bg-gray-800 rounded-3xl border border-app-border dark:border-gray-700 p-8 shadow-sm overflow-hidden"
                        >
                            <p className="text-xs font-black uppercase tracking-widest text-app-muted mb-6">
                                {contact.info.social_heading}
                            </p>
                            <div className="flex flex-row gap-3">
                                {socialLinks.whatsappChannel && (
                                    <a
                                        href={socialLinks.whatsappChannel}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="WhatsApp"
                                        className="w-12 h-12 flex items-center justify-center rounded-2xl text-white bg-[#25D366] shadow-lg shadow-green-500/20 hover:shadow-green-500/30 hover:-translate-y-1 transition-all duration-300"
                                    >
                                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                                            <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.46 0 .08 5.37.08 11.98c0 2.11.55 4.17 1.6 5.99L0 24l6.2-1.63a11.92 11.92 0 0 0 5.86 1.5h.01c6.6 0 11.98-5.37 11.98-11.98 0-3.2-1.25-6.21-3.53-8.4ZM12.07 21.8a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.68.97.98-3.59-.23-.37a9.78 9.78 0 0 1-1.5-5.24c0-5.42 4.41-9.82 9.84-9.82 2.62 0 5.09 1.02 6.94 2.88a9.75 9.75 0 0 1 2.88 6.94c0 5.42-4.42 9.82-9.84 9.82Zm5.39-7.36c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.28-.47-2.44-1.49-.9-.8-1.5-1.78-1.67-2.08-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.12 3.24 5.14 4.55.72.31 1.29.5 1.73.64.73.23 1.39.2 1.91.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z" />
                                        </svg>
                                    </a>
                                )}
                                {socialLinks.instagram && (
                                    <a
                                        href={socialLinks.instagram}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Instagram"
                                        className="w-12 h-12 flex items-center justify-center rounded-2xl text-white bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] shadow-lg shadow-pink-500/20 hover:shadow-pink-500/30 hover:-translate-y-1 transition-all duration-300"
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                                            <circle cx="12" cy="12" r="4" />
                                            <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
                                        </svg>
                                    </a>
                                )}
                                {socialLinks.facebook && (
                                    <a
                                        href={socialLinks.facebook}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Facebook"
                                        className="w-12 h-12 flex items-center justify-center rounded-2xl text-white bg-[#1877F2] shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 hover:-translate-y-1 transition-all duration-300"
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                                        </svg>
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* ── Right: form ── */}
                <motion.div {...fadeInUp} transition={{ delay: 0.2 }} className="md:col-span-3">
                    <div className="bg-white dark:bg-gray-800 rounded-[2rem] border border-app-border dark:border-gray-700 p-8 md:p-12 shadow-xl shadow-gray-200/50 dark:shadow-none relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-app-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

                        <h2 className="text-xl md:text-2xl font-black text-app-text dark:text-white mb-8">
                            {contactSection.data?.info?.form_title || contact.info.form_title}
                        </h2>

                        <form onSubmit={handleSubmit} noValidate className="space-y-6">
                            <div className="space-y-1.5">
                                <label htmlFor="dzSubject" className={labelClass}>{contact.form_labels.subject}</label>
                                <div className="relative">
                                    <select
                                        id="dzSubject"
                                        name="dzSubject"
                                        value={formData.dzSubject}
                                        onChange={handleChange}
                                        className={cn(inputClass, 'appearance-none pr-10 cursor-pointer')}
                                    >
                                        {contact.subjects.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <svg
                                            className="w-4 h-4 text-app-muted"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M6 9l6 6 6-6" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label htmlFor="dzName" className={labelClass}>
                                        {contact.form_labels.name} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="dzName"
                                        name="dzName"
                                        required
                                        type="text"
                                        placeholder={contact.form_placeholders.name}
                                        value={formData.dzName}
                                        onChange={handleChange}
                                        className={cn(inputClass, errors.dzName && 'border-red-500 focus:ring-red-500/20 focus:border-red-500')}
                                    />
                                    {errors.dzName && <p className="text-red-500 text-xs mt-1">{errors.dzName}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <label htmlFor="dzEmail" className={labelClass}>
                                        {contact.form_labels.email} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="dzEmail"
                                        name="dzEmail"
                                        required
                                        type="email"
                                        placeholder={contact.form_placeholders.email}
                                        value={formData.dzEmail}
                                        onChange={handleChange}
                                        className={cn(inputClass, errors.dzEmail && 'border-red-500 focus:ring-red-500/20 focus:border-red-500')}
                                    />
                                    {errors.dzEmail && <p className="text-red-500 text-xs mt-1">{errors.dzEmail}</p>}
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="dzPhoneNumber" className={labelClass}>{contact.form_labels.phone}</label>
                                <input
                                    id="dzPhoneNumber"
                                    name="dzPhoneNumber"
                                    type="tel"
                                    placeholder={contact.form_placeholders.phone}
                                    value={formData.dzPhoneNumber}
                                    onChange={handleChange}
                                    className={inputClass}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="dzMessage" className={labelClass}>
                                    {contact.form_labels.message} <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    id="dzMessage"
                                    name="dzMessage"
                                    required
                                    rows={5}
                                    placeholder={contact.form_placeholders.message}
                                    value={formData.dzMessage}
                                    onChange={handleChange}
                                    className={cn(inputClass, 'resize-none', errors.dzMessage && 'border-red-500 focus:ring-red-500/20 focus:border-red-500')}
                                />
                                {errors.dzMessage && <p className="text-red-500 text-xs mt-1">{errors.dzMessage}</p>}
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting}
                                fullWidth
                                size="lg"
                            >
                                {submitting ? (
                                    <div className="flex items-center justify-center gap-3">
                                        <SpinnerIcon className="w-5 h-5" />
                                        {contact.form_labels.submitting}
                                    </div>
                                ) : (
                                    contact.form_labels.submit
                                )}
                            </Button>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
        </>
    );
};

export default Contact;
